// ─── Trail Reviews Query & Mutation Hooks ───────────────────────────
// React Query hooks for trail reviews.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import {
  fetchTrailReviews,
  submitTrailReview,
  type TrailReview,
} from '@/lib/api/reviews';

/** Fetch reviews for a trail. Enabled only when trailId is provided. */
export function useTrailReviews(trailId: string | undefined, token: string | undefined) {
  return useQuery({
    queryKey: queryKeys.trails.reviews(trailId ?? ''),
    queryFn: () => fetchTrailReviews(trailId!, token),
    enabled: !!trailId,
    staleTime: 2 * 60 * 1000, // 2 min
  });
}

/** Submit a review for a trail. Invalidates the reviews cache on success. */
export function useSubmitReview(trailId: string | undefined, token: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (review: { rating: number; content?: string }) => {
      if (!trailId || !token) throw new Error('Missing trail ID or auth token');
      return submitTrailReview(trailId, token, review);
    },
    onSuccess: () => {
      // Invalidate reviews to refetch with the new review
      queryClient.invalidateQueries({ queryKey: queryKeys.trails.reviews(trailId ?? '') });
      // Also invalidate the trail detail since rating/reviewCount may have changed
      if (trailId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.trails.detail(trailId) });
      }
    },
  });
}